<!--
  Trenching & Excavation Evaluation & Work Plan

  3 steps:
  1. General Info — employer (default: MacDonald Miller), date, site, competent person, yes/no questions
  2. Site Conditions — 5 sections: Equipment Used (conditional details), Soil Classification,
     Underground Installations (Other checkbox + conditional text, 45-day warning),
     Hazardous Atmospheres, Surface & Hazard Protection
  3. Access, Shoring & Completion — 4 sections: Trench Access/Egress, Shoring Plan,
     Inspections acknowledgement (required), Training acknowledgement + employee names
     Footer: jobsite warning card
-->

<app-form-layout
  [title]="formTitle"
  [form]="form"
  [icon]="formIcon"
  [disabled]="submitting"
  [onSubmit]="onSubmit.bind(this)"
  [steps]="steps"
  [currentStep]="currentStep()"
  [completedSteps]="completedSteps()"
  [canNavigateToStep]="canNavigateToStep.bind(this)"
  [isStepCompleted]="isStepCompleted.bind(this)"
  [onNextStep]="onNextStep.bind(this)"
  [onPreviousStep]="onPreviousStep.bind(this)"
  [getProgressPercentage]="getProgressPercentage.bind(this)"
>
  <div
    *ngIf="form && fieldMappings"
    formContent
    [formGroup]="form"
    class="form-content"
  >

    <!-- ============================================================ -->
    <!-- Step 1 — General Information                                 -->
    <!-- ============================================================ -->
    <div *ngIf="currentStep() === steps[0].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[0].title }}</h1>
            <p>{{ steps[0].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="generalInfoFormGroup"
                [control]="generalInfoFormGroup.get('employerName')!"
                [label]="fieldMappings['employerName']"
                [requiredField]="true"
                placeholder="Enter employer name"
              ></app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-date-input
                [templateFormGroup]="generalInfoFormGroup"
                [control]="generalInfoFormGroup.get('date')!"
                [label]="fieldMappings['date']"
                [requiredField]="true"
                [today]="true"
              ></app-date-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="generalInfoFormGroup"
                [control]="generalInfoFormGroup.get('jobsiteNameAddress')!"
                [label]="fieldMappings['jobsiteNameAddress']"
                [requiredField]="true"
                placeholder="Enter jobsite name and address"
              ></app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="generalInfoFormGroup"
                [control]="generalInfoFormGroup.get('competentPersonName')!"
                [label]="fieldMappings['competentPersonName']"
                [requiredField]="true"
                placeholder="Enter name"
              ></app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="generalInfoFormGroup"
                [control]="generalInfoFormGroup.get('utilityLocatesObtained')!"
                [label]="fieldMappings['utilityLocatesObtained']"
                [requiredField]="true"
                [options]="yesNoOptions"
                [orientation]="'horizontal'"
              ></app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row *ngIf="generalInfoFormGroup.get('utilityLocatesObtained')?.value === 'No'">
            <ion-card color="danger" class="information-card">
              <ion-card-content>
                <ion-text color="light">
                  <h5><strong><ion-icon name="hand-left-outline"></ion-icon>&nbsp;Stop! Until a locate is completed.</strong></h5>
                </ion-text>
              </ion-card-content>
            </ion-card>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="generalInfoFormGroup"
                [control]="generalInfoFormGroup.get('authorityToStopWork')!"
                [label]="fieldMappings['authorityToStopWork']"
                [requiredField]="true"
                [options]="yesNoOptions"
                [orientation]="'horizontal'"
              ></app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row *ngIf="generalInfoFormGroup.get('authorityToStopWork')?.value === 'No'">
            <ion-card color="danger" class="information-card">
              <ion-card-content>
                <ion-text color="light">
                  <h5><strong><ion-icon name="hand-left-outline"></ion-icon>&nbsp;Stop! Assign a Competent Person to oversee the operation.</strong></h5>
                </ion-text>
              </ion-card-content>
            </ion-card>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- ============================================================ -->
    <!-- Step 2 — Site Conditions & Hazards                           -->
    <!-- sections[0] Equipment Used                                   -->
    <!-- sections[1] Soil Classification                              -->
    <!-- sections[2] Underground Installations                        -->
    <!-- sections[3] Hazardous Atmospheres                            -->
    <!-- sections[4] Surface & Hazard Protection                      -->
    <!-- ============================================================ -->
    <div *ngIf="currentStep() === steps[1].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[1].title }}</h1>
            <p>{{ steps[1].description }}</p>
          </ion-label>
        </ion-item-divider>

        <!-- Section: Equipment Used -->
        <ion-item-divider class="form-section-title">
          <ion-label>
            <h2>{{ steps[1].sections?.[0]?.title }}</h2>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="equipmentUsedFormGroup"
                [checkboxes]="equipmentUsedCheckboxes"
                [label]="fieldMappings['equipmentUsed']"
                [requiredField]="false"
              ></app-checkbox-input>
            </ion-col>
          </ion-row>

          <!-- Hydraulic shoring details — conditional on hydraulicShoringInstalled -->
          <ng-container *ngIf="equipmentUsedFormGroup.get('hydraulicShoringInstalled')?.value">
            <ion-row>
              <ion-col>
                <app-text-input
                  [templateFormGroup]="siteConditionsFormGroup"
                  [control]="siteConditionsFormGroup.get('hydraulicShoringMfr')!"
                  [label]="fieldMappings['hydraulicShoringMfr']"
                  [requiredField]="true"
                  placeholder="Enter manufacturer"
                ></app-text-input>
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col>
                <app-text-input
                  [templateFormGroup]="siteConditionsFormGroup"
                  [control]="siteConditionsFormGroup.get('hydraulicShoringSerialNumber')!"
                  [label]="fieldMappings['hydraulicShoringSerialNumber']"
                  [requiredField]="true"
                  placeholder="Enter serial number"
                ></app-text-input>
              </ion-col>
            </ion-row>
          </ng-container>

          <!-- Engineer name — conditional on engineeredSystem -->
          <ion-row *ngIf="equipmentUsedFormGroup.get('engineeredSystem')?.value">
            <ion-col>
              <app-text-input
                [templateFormGroup]="siteConditionsFormGroup"
                [control]="siteConditionsFormGroup.get('engineeredSystemEngineerName')!"
                [label]="fieldMappings['engineeredSystemEngineerName']"
                [requiredField]="true"
                placeholder="Enter engineer name"
              ></app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>

        <!-- Section: Soil Classification -->
        <ion-item-divider class="form-section-title">
          <ion-label>
            <h2>{{ steps[1].sections?.[1]?.title }}</h2>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="siteConditionsFormGroup"
                [control]="siteConditionsFormGroup.get('soilClassification')!"
                [label]="fieldMappings['soilClassification']"
                [requiredField]="true"
                [options]="soilClassificationOptions"
                [orientation]="'vertical'"
              ></app-radio-group-input>
            </ion-col>
          </ion-row>
        </ion-grid>

        <!-- Section: Underground Installations -->
        <ion-item-divider class="form-section-title">
          <ion-label>
            <h2>{{ steps[1].sections?.[2]?.title }}</h2>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-card color="warning" class="information-card">
              <ion-card-content>
                <ion-text>
                  <h5><strong><ion-icon name="warning-outline"></ion-icon>&nbsp;NOTE: Locates are only valid for 45 days.</strong><br><strong>Oregon – Locates are only valid for 30 days</strong></h5>
                </ion-text>
              </ion-card-content>
            </ion-card>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="undergroundInstallationsFormGroup"
                [checkboxes]="undergroundInstallationsCheckboxes"
                [label]="fieldMappings['undergroundInstallations']"
                [requiredField]="false"
              ></app-checkbox-input>
            </ion-col>
          </ion-row>

          <!-- Underground Other description — conditional on undergroundOther checkbox -->
          <ion-row *ngIf="undergroundInstallationsFormGroup.get('undergroundOther')?.value">
            <ion-col>
              <app-text-input
                [templateFormGroup]="siteConditionsFormGroup"
                [control]="siteConditionsFormGroup.get('undergroundOtherDescription')!"
                [label]="fieldMappings['undergroundOtherDescription']"
                [requiredField]="undergroundInstallationsFormGroup.get('undergroundOther')?.value"
                placeholder="Specify other underground installations"
              ></app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="siteConditionsFormGroup"
                [control]="siteConditionsFormGroup.get('undergroundInstallationProtectionMethod')!"
                [label]="fieldMappings['undergroundInstallationProtectionMethod']"
     
                [rows]="3"
                placeholder="Describe protection method"
              ></app-textarea-input>
            </ion-col>
          </ion-row>
        </ion-grid>

        <!-- Section: Hazardous Atmospheres -->
        <ion-item-divider class="form-section-title">
          <ion-label>
            <h2>{{ steps[1].sections?.[3]?.title }}</h2>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="atmosphericMonitoringFormGroup"
                [checkboxes]="atmosphericMonitoringCheckboxes"
                [label]="fieldMappings['atmosphericHazards']"
                [requiredField]="false"
              ></app-checkbox-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="siteConditionsFormGroup"
                [control]="siteConditionsFormGroup.get('ventilationFans')!"
                [label]="fieldMappings['ventilationFans']"
                [requiredField]="false"
                [options]="yesNoOtherOptions"
                [orientation]="'horizontal'"
              ></app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row *ngIf="siteConditionsFormGroup.get('ventilationFans')?.value === 'Other'">
            <ion-col>
              <app-text-input
                [templateFormGroup]="siteConditionsFormGroup"
                [control]="siteConditionsFormGroup.get('ventilationFansOther')!"
                [label]="fieldMappings['ventilationFansOther']"
                [requiredField]="true"
                placeholder="Describe ventilation arrangement"
              ></app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>

        <!-- Section: Surface & Hazard Protection -->
        <ion-item-divider class="form-section-title">
          <ion-label>
            <h2>{{ steps[1].sections?.[4]?.title }}</h2>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="siteConditionsFormGroup"
                [control]="siteConditionsFormGroup.get('surfaceEncumbranceProtectionMethod')!"
                [label]="fieldMappings['surfaceEncumbranceProtectionMethod']"
                [requiredField]="true"
                [rows]="3"
                placeholder="Describe protection method"
              ></app-textarea-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="siteConditionsFormGroup"
                [control]="siteConditionsFormGroup.get('adjacentStructureStabilizationMethod')!"
                [label]="fieldMappings['adjacentStructureStabilizationMethod']"
                [requiredField]="true"
                [rows]="3"
                placeholder="Describe stabilization method"
              ></app-textarea-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="siteConditionsFormGroup"
                [control]="siteConditionsFormGroup.get('hazardProtectionMethod')!"
                [label]="fieldMappings['hazardProtectionMethod']"
                [requiredField]="true"
                [rows]="3"
                placeholder="Describe hazard protection method"
              ></app-textarea-input>
            </ion-col>
          </ion-row>
        </ion-grid>

      </ion-list>
    </div>

    <!-- ============================================================ -->
    <!-- Step 3 — Access, Shoring & Completion                        -->
    <!-- sections[0] Trench Access / Egress                           -->
    <!-- sections[1] Shoring Plan                                     -->
    <!-- sections[2] Inspections                                      -->
    <!-- sections[3] Training                                         -->
    <!-- ============================================================ -->
    <div *ngIf="currentStep() === steps[2].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[2].title }}</h1>
            <p>{{ steps[2].description }}</p>
          </ion-label>
        </ion-item-divider>

        <!-- Section: Trench Access / Egress -->
        <ion-item-divider class="form-section-title">
          <ion-label>
            <h2>{{ steps[2].sections?.[0]?.title }}</h2>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="trenchAccessFormGroup"
                [checkboxes]="trenchAccessCheckboxes"
                [label]="fieldMappings['trenchAccess']"
                [requiredField]="false"
              ></app-checkbox-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="accessShoringCompletionFormGroup"
                [control]="accessShoringCompletionFormGroup.get('accessEgressOtherDescription')!"
                [label]="fieldMappings['accessEgressOtherDescription']"
                [requiredField]="false"
                [rows]="2"
                placeholder="Describe alternative access/egress method"
              ></app-textarea-input>
            </ion-col>
          </ion-row>
        </ion-grid>

        <!-- Section: Shoring Plan -->
        <ion-item-divider class="form-section-title">
          <ion-label>
            <h2>{{ steps[2].sections?.[1]?.title }}</h2>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="accessShoringCompletionFormGroup"
                [control]="accessShoringCompletionFormGroup.get('shoringPlanDescription')!"
                [label]="fieldMappings['shoringPlanDescription']"
                [requiredField]="true"
                [rows]="4"
                placeholder="Describe shoring system, installation, removal, and protective measures"
              ></app-textarea-input>
            </ion-col>
          </ion-row>
              <!-- Footer warning -->
          <ion-row>
            <ion-card color="warning" class="information-card">
              <ion-card-content>
                <ion-text>
                  <h5><strong><ion-icon name="warning-outline"></ion-icon>&nbsp;Note: Excavations greater than 20’ deep require a RPE stamped 
drawing.</strong></h5>
                </ion-text>
              </ion-card-content>
            </ion-card>
          </ion-row>
          <!-- <ion-row>
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="rpeFormGroup"
                [checkboxes]="rpeCheckboxes"
                [label]="''"
                [requiredField]="false"
              ></app-checkbox-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="soilsEngineerFormGroup"
                [checkboxes]="soilsEngineerCheckboxes"
                [label]="''"
                [requiredField]="false"
              ></app-checkbox-input>
            </ion-col>
          </ion-row> -->
        </ion-grid>

        <!-- Section: Inspections -->
        <ion-item-divider class="form-section-title">
          <ion-label>
            <h2>{{ steps[2].sections?.[2]?.title }}</h2>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="inspectionsFormGroup"
                [checkboxes]="inspectionsCheckboxes"
                [label]="fieldMappings['inspectionsAcknowledgedLabel']"
                [requiredField]="true"
              ></app-checkbox-input>
            </ion-col>
          </ion-row>
        </ion-grid>

        <!-- Section: Training -->
        <ion-item-divider class="form-section-title">
          <ion-label>
            <h2>{{ steps[2].sections?.[3]?.title }}</h2>
          </ion-label>
        </ion-item-divider>

        <ion-grid>

          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="accessShoringCompletionFormGroup"
                [control]="accessShoringCompletionFormGroup.get('trainedEmployees')!"
                [label]="fieldMappings['trainedEmployees']"
                [requiredField]="true"
                [rows]="4"
                placeholder="List trained employee names, one per line"
              ></app-textarea-input>
            </ion-col>
          </ion-row>

          <!-- Footer warning -->
          <ion-row>
            <ion-card color="warning" class="information-card">
              <ion-card-content>
                <ion-text>
                  <h5><strong><ion-icon name="warning-outline"></ion-icon>&nbsp;NOTE: The work plan must be kept on the jobsite and made available to safety regulators upon request.</strong></h5>
                </ion-text>
              </ion-card-content>
            </ion-card>
          </ion-row>

        </ion-grid>

      </ion-list>
    </div>

  </div>
</app-form-layout>
